import { useState, useEffect, useCallback } from 'react';
import leagueService, { League } from '../services/leagueService';

interface UseLeaguesOptions {
  activeOnly?: boolean;
}

interface UseLeaguesReturn {
  leagues: League[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useLeagues = (options: UseLeaguesOptions = {}): UseLeaguesReturn => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLeagues = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Siempre obtener todas las ligas para aplicar nuestra lógica de cálculo
      const data = await leagueService.getLeagues();
      
      // Calcular is_active basándose en end_date
      const leaguesWithCalculatedStatus = data.map(league => ({
        ...league,
        is_active: calculateIsActive(league.end_date)
      }));
      
      // Si activeOnly es true, filtrar solo las ligas activas
      const finalLeagues = options.activeOnly 
        ? leaguesWithCalculatedStatus.filter(league => league.is_active)
        : leaguesWithCalculatedStatus;
      
      setLeagues(finalLeagues);
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching leagues:', err);
    } finally {
      setLoading(false);
    }
  }, [options.activeOnly]);

  // Función para calcular si una liga está activa
  const calculateIsActive = (endDate?: string): boolean => {
    if (!endDate) {
      // Si no hay end_date, la liga está activa
      console.log('Liga sin end_date, marcada como activa');
      return true;
    }
    
    const today = new Date();
    // Obtener solo la fecha actual en formato YYYY-MM-DD para comparación precisa
    const todayString = today.toISOString().split('T')[0];
    
    // La liga está activa si la fecha de fin es HOY o POSTERIOR a hoy
    // Esto significa que la liga está activa durante todo su último día
    const isActive = endDate >= todayString;
    console.log(`Liga end_date: ${endDate}, today: ${todayString}, isActive: ${isActive} (activa si end_date >= today)`);
    
    return isActive;
  };

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  return {
    leagues,
    loading,
    error,
    refetch: fetchLeagues,
  };
};
